import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { Form, Input } from '@engloba-tech/englobity';
import { NAME_SPACE } from 'i18n';
import {
  ANALYTICS_REPORT_TYPE,
  MONTH_TYPE,
  Dropzone,
  ButtonDownloadFile,
  isSkeletonLoading,
  SelectInput,
  enumHelper
} from 'shared';
import { useAnalyticsReportForm } from './useAnalyticsReportForm';
import { useFormAnalyticsReportStyles } from './formAnalyticsReport.styles';

export function FormAnalyticsReport({ formRef, onSubmit, report, errors, onChangeInput, isReadOnly = false }) {
  const { t } = useTranslation(NAME_SPACE.ANALYTICS_REPORTS);
  const classes = useFormAnalyticsReportStyles();
  const { reportInputs, handleChange, handleSelectFile, handleDeleteFile, handleDownload, submitted, setSubmitted } =
    useAnalyticsReportForm(report, onChangeInput);

  const reportTypes = useMemo(() => {
    return enumHelper.enumToCombo(ANALYTICS_REPORT_TYPE, t, 'analyticsReportTypes', true);
  }, [t]);

  const months = useMemo(() => {
    return enumHelper.enumToCombo(MONTH_TYPE, t, 'months', true);
  }, [t]);

  function handleSubmit() {
    setSubmitted(true);
    if (reportInputs.reportFileName === '' || reportInputs.reportFileName === undefined) return;
    onSubmit(reportInputs);
  }

  return (
    <>
      <Form
        className={classes.formReport}
        errors={errors}
        elementRef={formRef}
        onSubmit={handleSubmit}
        autoComplete="off"
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <Input
              isLoading={isSkeletonLoading(reportInputs.name)}
              required
              name={t('properties.name')}
              label={t('properties.name')}
              variant="outlined"
              disabled={isReadOnly}
              value={reportInputs.name || ''}
              onChange={handleChange('name')}
              inputProps={{ 'aria-label': t('properties.name') }}
              validators={['required', 'maxStringLength:255']}
              errorMessages={[t('validations.required'), t('common:validations.maxStringLength', { length: 255 })]}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={6}>
            <SelectInput
              name={t('properties.reportType')}
              label={t('properties.reportType')}
              elements={reportTypes}
              required
              value={reportInputs.reportType}
              disabled={isReadOnly}
              onChange={handleChange('reportType')}
              validators={['required']}
              errorMessages={[t('validations.required')]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <Input
              isLoading={isSkeletonLoading(reportInputs.year)}
              required
              name={t('properties.year')}
              label={t('properties.year')}
              variant="outlined"
              type="number"
              disabled={isReadOnly}
              value={reportInputs.year ?? ''}
              onChange={handleChange('year')}
              inputProps={{ 'aria-label': t('properties.year') }}
              validators={['required', 'minNumber:2000', 'maxNumber:2150']}
              errorMessages={[
                t('validations.required'),
                t('validations.minNumber', { minimum: 2000 }),
                t('validations.maxNumber', { maximum: 2150 })
              ]}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <SelectInput
              name={t('properties.month')}
              label={t('properties.month')}
              elements={months}
              required
              disabled={isReadOnly}
              value={reportInputs.month}
              onChange={handleChange('month')}
              validators={['required']}
              errorMessages={[t('validations.required')]}
            />
          </Grid>
          <Grid container alignItems="center" item xs={12} sm={12} md={12} lg={6}>
            {isReadOnly && !isSkeletonLoading(reportInputs.reportFileName) ? (
              <Input
                isLoading={isSkeletonLoading(reportInputs.reportFileName)}
                name={t('properties.reportFileName')}
                label={t('properties.reportFileName')}
                variant="outlined"
                disabled
                fullWidth
                className={classes.reportFileName}
                value={reportInputs.reportFileName ?? ''}
                inputProps={{ 'aria-label': t('properties.reportFileName') }}
              />
            ) : (
              <Dropzone
                isLoading={isSkeletonLoading(reportInputs.reportFileUrl)}
                accept={'application/pdf'}
                maxFiles={1}
                files={reportInputs.reportFileName ? [{ tag: reportInputs.reportFileName }] : []}
                onDrop={handleSelectFile}
                onDeleteFile={handleDeleteFile}
                labelDrop={t('common:upload.drop')}
                required
                submitted={submitted}
                errorMessage={t('validations.required')}
              />
            )}
            {!isSkeletonLoading(reportInputs.reportFileName) && reportInputs.reportFileUrl && (
              <ButtonDownloadFile onClick={() => handleDownload([reportInputs.reportFileUrl])} />
            )}
          </Grid>
        </Grid>
      </Form>
    </>
  );
}

FormAnalyticsReport.propTypes = {
  formRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]),
  onSubmit: PropTypes.func,
  errors: PropTypes.shape({
    message: PropTypes.string,
    detail: PropTypes.string
  }),
  report: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    reportType: PropTypes.string,
    month: PropTypes.number,
    year: PropTypes.number,
    reportFileUrl: PropTypes.string,
    reportFileName: PropTypes.string,
    reportFileMimeType: PropTypes.string
  }),
  onChangeInput: PropTypes.func,
  isReadOnly: PropTypes.bool
};
